// src/components/authentication/ForgotPasswordRequest.js
/* ------------------------------------------------------------------ */
/* ForgotPasswordRequest – send reset-link e-mail                      */
/* ------------------------------------------------------------------ */
/**
 * • Collects user’s e-mail → password reset function.
 * • Shows confirmation that a link was sent (even if the e-mail is unknown,
 *   to avoid account enumeration).
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { EMAIL_REGEX } from "../../utils/constants";
import usePopup from "../../hooks/usePopup";
import ErrorPopup from "../ErrorPopup";
import { useTranslation } from "react-i18next";

function getPasswordResetEndpoint() {
  const configuredBaseUrl = process.env.REACT_APP_FUNCTIONS_BASE_URL?.trim();
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID?.trim();
  const region =
    process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION?.trim() || "us-central1";
  const baseUrl = (
    configuredBaseUrl || (projectId && `https://${region}-${projectId}.cloudfunctions.net`)
  )?.replace(/\/+$/, "");

  if (!baseUrl) {
    throw new Error(
      "Password reset function URL is not configured. Set REACT_APP_FUNCTIONS_BASE_URL or REACT_APP_FIREBASE_PROJECT_ID.",
    );
  }

  return `${baseUrl}/requestPasswordReset`;
}

/* Component */
export default function ForgotPasswordRequest() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { popup, open: openPopup, close: closePopup } = usePopup();

  const handleSubmit = async (e) => {
    e.preventDefault();
    closePopup();

    const normalisedEmail = email.trim();

    if (!EMAIL_REGEX.test(normalisedEmail)) {
      openPopup({ message: t("forgotPassword.emailRegexError") });
      return;
    }

    try {
      const response = await fetch(getPasswordResetEndpoint(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: normalisedEmail,
          origin: window.location.origin,
        }),
      });

      if (!response.ok) {
        throw new Error(`Password reset request failed with ${response.status}`);
      }

      setEmail(normalisedEmail);
      setSent(true);
    } catch (err) {
      console.error("Password reset request failed:", err);
      openPopup({ message: t("forgotPassword.emailSendError") });
    }
  };

  return (
    <div className="w-full max-w-lg h-full p-4 flex flex-col justify-end md:justify-start items-center gap-4">
      {popup && <ErrorPopup popup={popup} onClose={closePopup} />}

      <img src="/pdx_logo.png" alt="pDx logo" className="h-24" />
      <h1 className="title-text">{t("forgotPassword.title")}</h1>

      {sent ? (
        <>
          <p className="text-center">
            {t("forgotPassword.exists1")}
            <strong>{email}</strong> {t("forgotPassword.exists2")}
          </p>
          <Link to="/auth/login" className="link">
            {t("forgotPassword.back")}
          </Link>
        </>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full">
          <input
            type="email"
            placeholder={t("forgotPassword.email")}
            className="custom-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button type="submit" className="primary-button">
            {t("forgotPassword.cta")}
          </button>
        </form>
      )}
    </div>
  );
}
